import { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';
import _ from 'lodash';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
  useGetApiCategoryTreeQuery,
  usePostApiArticlesStructureMutation,
} from '../../../redux/store/api/api';
import {
  selectAddStructureElementDialogOpened,
  selectContextCategory,
  setAddStructureElementDialogOpened,
  setContextCategory,
  setFocusedTreeItem,
} from '../../../redux/store/content/slice';
import { addMessage } from '../../../redux/store/layout/slice';
import { EXPANDED_CATEGORY_IDS } from '../../../shared/constants';
import CustomDialog from '../../dialogs/CustomDialog';
import Loader from '../../loader/Loader';

function AddStructureElementDialog() {
  const { t: translation } = useTranslation();
  const [expandedCategoryIds, setExpandedCategoryIds] = useSessionStorage<
    string[]
  >(EXPANDED_CATEGORY_IDS, []);
  const [elementName, setElementName] = useState('');
  const [isValidName, setIsValidName] = useState(true);
  const dialogShow = useAppSelector(selectAddStructureElementDialogOpened);
  const category = useAppSelector(selectContextCategory);
  const dispatch = useAppDispatch();
  const [addStructureElement, { isError, isLoading, error }] =
    usePostApiArticlesStructureMutation();
  const { refetch } = useGetApiCategoryTreeQuery();

  useEffect(() => {
    if (isError) {
      dispatch(
        addMessage({
          id: 'AddStructureElementError',
          variant: 'danger',
          messageKeyBody:
            error && 'data' in error ? error.data?.messageKey : 'unknownError',
        }),
      );
    }
  }, [isError]);

  const handleAddStructureElement = (): void => {
    if (elementName.trim().length === 0) {
      setIsValidName(false);
      return;
    }

    addStructureElement({
      categoryId: category.id || '',
      title: elementName.trim(),
    })
      .unwrap()
      .then((result) => {
        if (result.messageKey && result.messageKey !== '') {
          dispatch(
            addMessage({
              id: 'AddStructureElementSuccess',
              variant: 'success',
              messageKeyBody: result.messageKey,
            }),
          );
        }
        setExpandedCategoryIds(
          _.union(expandedCategoryIds, [category.id || '']),
        );
        setElementName('');
        dispatch(setAddStructureElementDialogOpened(false));
        dispatch(setContextCategory({}));
        setTimeout(() => {
          refetch();
        });
        dispatch(setFocusedTreeItem(result.resultObject?.id ?? ''));
      });
  };

  return (
    <CustomDialog
      titleId='AddStructureElementDialog'
      show={dialogShow}
      closeFunction={() => {
        setElementName('');
        setIsValidName(true);
        dispatch(setContextCategory({}));
        dispatch(setAddStructureElementDialogOpened(false));
      }}
      closeTitle={translation('cancel')}
      actionFunction={handleAddStructureElement}
      actionTitle={translation('add')}
      actionButtonDisabled={isLoading}
      dialogTitle={translation('createStructureElement')}>
      {isLoading && <Loader />}
      {!isLoading && (
        <>
          <p>{translation('fieldsAreRequiredLegend')}</p>
          <Form.Group className='mb-3' controlId='StructureElementName'>
            <Form.Label>{translation('structureElementName')}*</Form.Label>
            <Form.Control
              aria-describedby={
                isValidName ? undefined : 'StructureElementNameInputError'
              }
              isInvalid={!isValidName}
              required
              onChange={(e) => {
                setElementName(e.target.value);
                if (e.target.value.trim().length > 0) {
                  setIsValidName(true);
                }
              }}
              type='text'
              value={elementName}
            />
            <Form.Control.Feedback
              id='StructureElementNameInputError'
              type='invalid'>
              {translation('fieldNotEmpty')}
            </Form.Control.Feedback>
          </Form.Group>
        </>
      )}
    </CustomDialog>
  );
}

export default AddStructureElementDialog;
